import { useEffect, useState } from "react";
import {
  Table as CSDTable,
  Header,
  SpaceBetween,
  Box,
} from "@cloudscape-design/components-themed";
import { Link } from "react-router-dom";
import { LABELS } from "./constants";
import type { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hook";
import { useGetHoldingsInfosQuery } from "../../features/api";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Pagination from "../UI/Pagination";
import Preference from "../UI/Preference";
import { COMMON_LABELS } from "../default-constants";

const columnDefinitions = [
  {
    id: "holdingscompanycode",
    header: LABELS.HOLDINGS_COMPANY_CODE,
    cell: (e: any) => (
      <Link to={"/holdings-update/" + e.companyCode + "/" + e.name}>
        {e.companyCode}
      </Link>
    ),
  },
  {
    id: "holdingscorporatenumber",
    header: LABELS.HOLDINGS_CORPORATE_NUMBER,
    cell: (e: any) => e.corporateNumber,
  },
  {
    id: "holdingscompanyname",
    header: LABELS.HOLDINGS_COMPANY_NAME,
    cell: (e: any) => e.name,
  },
];

/* 
ホールディングス案件の一覧表
 */
const Table = () => {
  const searchCondition = useAppSelector(
    (state: RootState) => state.holdingsSearchCondition
  );

  // データの取得
  const { data, isFetching, refetch } =
    useGetHoldingsInfosQuery(searchCondition);

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, 1, preferences.pageSize));
      setAllItems(data);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (!data) return null;

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(data.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <Header>
            {LABELS.TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
          <SpaceBetween direction="horizontal" size="m"></SpaceBetween>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
